import { useEffect, useRef, useState } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation, useNavigationType } from 'react-router-dom';

import { mainMenuParentRoutePath, mainMenuRoutePath } from './routePath';

import useToken from '../common/hooks/useToken';
import useUserName from '../common/hooks/useUserName';
import usePcode from '../common/hooks/usePcode';
import useComCode from '../common/hooks/useComCode';
import useComName from '../common/hooks/useComName';
import usePcodeConfirmed from '../common/hooks/usePcodeConfirmed';

import LogIn from '../pages/logIn';
import SelectPcenter from '../pages/selectPcenter';
import MainMenu from '../pages/mainMenu';

const selectPcenterRoutePath = '/selectpcenter';

export default function App() {
    const { token, setToken } = useToken();
    const { setLogedUserName } = useUserName();
    const { loggedPcode, setLoggedPCode } = usePcode();
    const { loggedComCode, setLoggedComCode } = useComCode();
    const { loggedComName, setLoggedComName } = useComName();
    const { pcodeConfirmed, setPcodeConfirmed } = usePcodeConfirmed();

    const navigate = useNavigate();
    const location = useLocation();
    const navigationType = useNavigationType();

    // Whether the browser Back button has brought the user back to the
    // SelectPcenter screen. Kept in memory only (not persisted) so a page
    // refresh here still falls through to MainMenu as usual.
    const [showSelectPcenter, setShowSelectPcenter] = useState(false);
    const hasMounted = useRef(false);

    // Give SelectPcenter its own history entry (via replace, so it doesn't
    // pile up on re-renders) so there's somewhere for Back to land once the
    // user moves on to MainMenu.
    useEffect(() => {
        if (token && !pcodeConfirmed && location.pathname !== selectPcenterRoutePath) {
            navigate(selectPcenterRoutePath, { replace: true });
        }
    }, [token, pcodeConfirmed]);

    // React to Back/Forward (POP) navigation landing on that entry. The
    // initial mount is also reported as POP by react-router, so it's
    // ignored here to avoid re-showing SelectPcenter on a plain refresh.
    useEffect(() => {
        if (!hasMounted.current) {
            hasMounted.current = true;
            return;
        }
        if (navigationType !== 'POP') return;

        if (pcodeConfirmed && location.pathname === selectPcenterRoutePath) {
            setShowSelectPcenter(true);
        } else if (location.pathname !== selectPcenterRoutePath) {
            setShowSelectPcenter(false);
        }
    }, [location, navigationType]);

    const logOut = () => {
        setToken('');
        setLogedUserName('');
        setLoggedPCode('');
        setLoggedComCode('');
        setLoggedComName('');
        setPcodeConfirmed(false);
    }

    if (!token)
        return <LogIn setToken={setToken} setLogedUserName={setLogedUserName} setLoggedPcode={setLoggedPCode} setLoggedComCode={setLoggedComCode} setLoggedComName={setLoggedComName} />;

    if (!pcodeConfirmed || showSelectPcenter)
        return <SelectPcenter
            comCode={loggedComCode}
            comName={loggedComName}
            defaultPcode={loggedPcode}
            setLoggedPcode={setLoggedPCode}
            setPcodeConfirmed={(confirmed) => {
                setShowSelectPcenter(false);
                setPcodeConfirmed(confirmed);
                if (confirmed) navigate(mainMenuRoutePath);
            }}
        />;

    return (
        <>
            <Routes>
                <Route path={'*'} element={<Navigate to={mainMenuRoutePath} />}></Route>
                <Route path={mainMenuParentRoutePath} element={<MainMenu logOut={logOut} />}></Route>
            </Routes>
        </>
    )
}